import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Paperclip, Send, X } from 'lucide-react';
import moment from 'moment';

import { emailRegex } from '../../helper/regex';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

export type POEmailDraft = {
  to: string[];
  cc: string[];
  replyTo: string[];
  subject: string;
  body: string;
};

type SendPODialogProps = {
  open: boolean;
  poNumber: string;
  orderDate?: Date | string | null;
  supplier: any;
  account: any;
  user: any;
  sidemark: string;
  isSending: boolean;
  requiresSaveBeforeSend?: boolean;
  disabledReason?: string;
  onOpenChange(open: boolean): void;
  onSend(draft: POEmailDraft): void;
};

const isValidEmail = (value: string) => emailRegex.test(String(value || '').trim());

const normalizeEmailList = (values: string[] = []) => {
  return [
    ...new Set(
      values
        .map((entry) => String(entry || '').trim().toLowerCase())
        .filter(Boolean)
    ),
  ];
};

const splitEmailEntries = (value: any) => {
  if (Array.isArray(value)) {
    return value.flatMap((entry) => splitEmailEntries(entry));
  }
  return String(value || '')
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean);
};

const resolveSenderProfile = (sender?: any) => {
  const fallbackFirstName =
    typeof window !== 'undefined' ? String(localStorage.getItem('firstName') || '').trim() : '';
  const fallbackLastName =
    typeof window !== 'undefined' ? String(localStorage.getItem('lastName') || '').trim() : '';
  const fallbackEmail =
    typeof window !== 'undefined' ? String(localStorage.getItem('email') || '').trim() : '';

  const firstName = String(
    sender?.firstName || sender?.user?.firstName || fallbackFirstName || ''
  ).trim();
  const lastName = String(
    sender?.lastName || sender?.user?.lastName || fallbackLastName || ''
  ).trim();
  const email = String(sender?.email || sender?.user?.email || fallbackEmail || '').trim();

  return {
    id: sender?.id || sender?.user?.id || null,
    firstName,
    lastName,
    fullName: [firstName, lastName].filter(Boolean).join(' ').trim(),
    email: isValidEmail(email) ? email : '',
  };
};

const resolveCompanyName = (account?: any) => {
  return String(
    account?.clientFacingInfo?.companyName ||
      account?.facingCompanyName ||
      account?.billingCompanyName ||
      account?.billingInfo?.company ||
      account?.companyName ||
      account?.company ||
      ''
  ).trim();
};

const resolveAccountEmail = (account?: any) => {
  return String(
    account?.clientFacingInfo?.email ||
      account?.facingEmail ||
      account?.billingInfo?.email ||
      account?.billingEmail ||
      account?.email ||
      ''
  ).trim();
};

const resolveSupplierEmails = (supplier?: any) => {
  return normalizeEmailList(
    splitEmailEntries(supplier?.shippingAddress?.email || supplier?.billingAddress?.email || '')
  ).filter(isValidEmail);
};

const resolveCcDefaults = ({ account, user }: { account?: any; user?: any }) => {
  const sender = resolveSenderProfile(user);
  const accountEmail = resolveAccountEmail(account);
  const accountEstimateSending = account?.accountEstimateSending || {};
  const includeSenderInCc = accountEstimateSending?.cc?.sender !== false;
  const includeClientFacingInCc = !!accountEstimateSending?.cc?.clientFacing;

  return normalizeEmailList([
    ...(includeSenderInCc && sender.email ? [sender.email] : []),
    ...(includeClientFacingInCc && isValidEmail(accountEmail) ? [accountEmail] : []),
    ...splitEmailEntries(account?.additionalCcEmails || []),
  ]).filter(isValidEmail);
};

const resolveReplyToDefaults = ({ account, user }: { account?: any; user?: any }) => {
  const sender = resolveSenderProfile(user);
  const accountEmail = resolveAccountEmail(account);
  const accountEstimateSending = account?.accountEstimateSending || {};
  const includeClientFacingInReplyTo = !!accountEstimateSending?.replyTo?.clientFacing;

  return normalizeEmailList([
    ...(sender.email ? [sender.email] : []),
    ...(includeClientFacingInReplyTo && isValidEmail(accountEmail) ? [accountEmail] : []),
    ...splitEmailEntries(account?.additionalReplyToEmails || []),
  ]).filter(isValidEmail);
};

const displayPoNumber = (poNumber: string) => {
  const normalized = String(poNumber || '').trim() || 'PO';
  return normalized.toUpperCase().startsWith('PO-') ? normalized : `PO-${normalized}`;
};

const buildEmailDraft = ({
  poNumber,
  supplier,
  account,
  user,
  sidemark,
}: Pick<SendPODialogProps, 'poNumber' | 'supplier' | 'account' | 'user' | 'sidemark'>) => {
  const sender = resolveSenderProfile(user);
  const companyName = resolveCompanyName(account);
  const resolvedPoNumber = displayPoNumber(poNumber);

  return {
    to: resolveSupplierEmails(supplier),
    cc: resolveCcDefaults({ account, user }),
    replyTo: resolveReplyToDefaults({ account, user }),
    subject: `${resolvedPoNumber} from ${companyName}`,
    body: [
      `Please see the attached ${resolvedPoNumber} for ${String(sidemark || '').trim() || '\u2014'}.`,
      'Sincerely,',
      sender.fullName,
      sender.email,
      companyName,
    ].join('\n'),
  };
};

const EmailChipInput = ({
  label,
  description,
  values,
  onChange,
  placeholder,
  disabled,
}: {
  label: string;
  description?: string;
  values: string[];
  onChange(values: string[]): void;
  placeholder: string;
  disabled?: boolean;
}) => {
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');

  const commitValues = useCallback(
    (rawValue: string) => {
      const entries = splitEmailEntries(rawValue);
      if (!entries.length) {
        setInputValue('');
        setError('');
        return;
      }

      const validEntries = entries.filter(isValidEmail);
      const invalidEntries = entries.filter((entry) => !isValidEmail(entry));

      if (validEntries.length) {
        onChange(normalizeEmailList([...values, ...validEntries]));
      }

      setInputValue('');
      setError(
        invalidEntries.length
          ? `Invalid email${invalidEntries.length > 1 ? 's' : ''}: ${invalidEntries.join(', ')}`
          : ''
      );
    },
    [onChange, values]
  );

  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium">{label}</Label>
      {description ? <p className="text-xs text-muted-foreground">{description}</p> : null}
      <div className="mb-1.5 flex flex-wrap gap-1.5">
        {values.map((value) => (
          <span
            key={value}
            className="inline-flex max-w-full items-center gap-1 rounded-full border border-border bg-muted px-2.5 py-0.5 text-xs text-foreground"
          >
            <span className="truncate">{value}</span>
            <button
              type="button"
              aria-label={`Remove ${value}`}
              className="text-muted-foreground hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
              onClick={() => onChange(values.filter((entry) => entry !== value))}
              disabled={disabled}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>
      <Input
        value={inputValue}
        disabled={disabled}
        placeholder={placeholder}
        className="h-9"
        onChange={(event) => {
          const nextValue = event.target.value;
          if (nextValue.includes(',')) {
            commitValues(nextValue);
          } else {
            setInputValue(nextValue);
            setError('');
          }
        }}
        onBlur={() => commitValues(inputValue)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ',') {
            event.preventDefault();
            commitValues(inputValue);
          }
        }}
      />
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
};

const SendPODialog = ({
  open,
  poNumber,
  orderDate,
  supplier,
  account,
  user,
  sidemark,
  isSending,
  requiresSaveBeforeSend = true,
  disabledReason,
  onOpenChange,
  onSend,
}: SendPODialogProps) => {
  const [draft, setDraft] = useState<POEmailDraft>(() =>
    buildEmailDraft({ poNumber, supplier, account, user, sidemark })
  );

  useEffect(() => {
    if (!open) return;
    setDraft(buildEmailDraft({ poNumber, supplier, account, user, sidemark }));
  }, [account, open, poNumber, sidemark, supplier, user]);

  const summaryLine = useMemo(() => {
    const dateLabel = orderDate ? moment(orderDate).format('MMMM D, YYYY') : moment().format('MMMM D, YYYY');
    return `${displayPoNumber(poNumber)} \u2014 ${dateLabel}`;
  }, [orderDate, poNumber]);

  const attachmentFileName = `${displayPoNumber(poNumber)}.pdf`;
  const sendDisabled = isSending || !!disabledReason || !normalizeEmailList(draft.to).length;
  const sendButtonLabel = requiresSaveBeforeSend ? 'Save & Send' : 'Send';
  const sendingButtonLabel = requiresSaveBeforeSend ? 'Saving & Sending...' : 'Sending...';

  return (
    <Dialog open={open} onOpenChange={(value) => !isSending && onOpenChange(value)}>
      <DialogContent
        className="max-h-[90vh] max-w-lg overflow-y-auto"
        showCloseButton={!isSending}
      >
        <DialogHeader>
          <DialogTitle>Send Purchase Order</DialogTitle>
          <DialogDescription>Send this PO to your supplier via email.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="rounded-lg border border-border bg-muted/10 p-3">
            <p className="text-sm font-semibold text-foreground">{summaryLine}</p>
          </div>

          <EmailChipInput
            label="Supplier Email"
            values={draft.to}
            onChange={(values) => setDraft((prev) => ({ ...prev, to: values }))}
            placeholder="Add email and press Enter..."
            disabled={isSending}
          />
          <EmailChipInput
            label="CC"
            description="Additional recipients who will be copied on this email."
            values={draft.cc}
            onChange={(values) => setDraft((prev) => ({ ...prev, cc: values }))}
            placeholder="Add email and press Enter..."
            disabled={isSending}
          />
          <EmailChipInput
            label="Reply-To"
            values={draft.replyTo}
            onChange={(values) => setDraft((prev) => ({ ...prev, replyTo: values }))}
            placeholder="Add email and press Enter..."
            disabled={isSending}
          />

          <div className="h-px bg-border" />

          <div className="space-y-3 rounded-lg border border-border bg-muted/5 p-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-muted-foreground">Subject</Label>
              <Input
                className="h-8 text-sm"
                value={draft.subject}
                onChange={(event) => setDraft((prev) => ({ ...prev, subject: event.target.value }))}
                disabled={isSending}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-muted-foreground">Body</Label>
              <textarea
                className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 font-mono text-xs leading-relaxed text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                value={draft.body}
                onChange={(event) => setDraft((prev) => ({ ...prev, body: event.target.value }))}
                disabled={isSending}
                rows={6}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-muted-foreground">Attachment</Label>
              <div className="inline-flex max-w-full items-center gap-2 rounded-md border border-border bg-background px-2.5 py-1.5 text-xs text-foreground">
                <Paperclip className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="truncate">{attachmentFileName}</span>
              </div>
            </div>
          </div>

          {disabledReason ? <p className="text-sm text-destructive">{disabledReason}</p> : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSending}>
            Cancel
          </Button>
          <Button
            type="button"
            className="bg-teal text-white hover:bg-teal-dark"
            onClick={() => onSend(draft)}
            disabled={sendDisabled}
            title={disabledReason || undefined}
          >
            {isSending ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            ) : (
              <Send className="mr-1.5 h-4 w-4" />
            )}
            {isSending ? sendingButtonLabel : sendButtonLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SendPODialog;
